"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnTransformNetWireGenerator = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematic");
const TransformNetWire_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformNetWire");
const TransformNetLabel_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformNetLabel");
const TransformSchematicDrawing_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematicDrawing");
const orcadLayers_1 = require("../../../orcadLayers");
const OrcadDsnBusUtils_1 = require("./OrcadDsnBusUtils");
const TransformProcessingResult_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProcessingResult");
const OrcadDsnSymbolUtils_1 = require("./OrcadDsnSymbolUtils");
const OrcadDsnUtils_1 = require("./OrcadDsnUtils");
const ModelText_1 = require("../../../../../ModelText");
class OrcadDsnTransformNetWireGenerator {
    constructor() {
        this.runsFor = [TransformSchematic_1.TransformSchematic];
    }
    *generate(parent, _rootContext) {
        const page = parent.hierarchyEntry.page;
        const signalEndpointToDimension = this.buildBusEntryDimensionMap(page);
        for (const netScalar of page.NetScalar ?? []) {
            yield* this.generateFromNetScalar(parent, netScalar);
        }
        for (const netBus of page.NetBus ?? []) {
            yield* this.generateFromNetBus(parent, netBus);
        }
        for (const busEntry of page.BusEntry ?? []) {
            let signalDimension = signalEndpointToDimension.get(`${busEntry.EndX},${busEntry.EndY}`);
            if (!signalDimension) {
                yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                    type: 'orcad-dsn-transformer',
                    message: `Could not find signal dimension for bus entry at ${busEntry.EndX},${busEntry.EndY} in ${parent.sourceFile} (${parent.designId})`,
                    severity: sigma_engine_shared_1.BoundaryLevel.Warning,
                });
                continue;
            }
            let busPosition = [busEntry.BusX, busEntry.BusY];
            let wirePosition = [busEntry.EndX, busEntry.EndY];
            if (!signalDimension) {
                signalDimension = signalEndpointToDimension.get(`${busEntry.BusX},${busEntry.BusY}`);
                if (signalDimension) {
                    busPosition = [busEntry.EndX, busEntry.EndY];
                    wirePosition = [busEntry.BusX, busEntry.BusY];
                }
            }
            yield new TransformNetWire_1.TransformNetWire(parent, busPosition, wirePosition, {
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire].id],
                drawing: {
                    type: 'segment',
                    start: busPosition,
                    end: wirePosition,
                    width: 0.3,
                },
            }, typeof signalDimension === 'object' ? signalDimension.name : undefined, undefined, typeof signalDimension === 'object' ?
                {
                    'bus-entry': {
                        busName: signalDimension.name,
                        memberName: signalDimension.name,
                        memberIndex: signalDimension.index,
                    },
                }
                : undefined);
            if (typeof signalDimension === 'object') {
                yield new TransformNetLabel_1.TransformNetLabel(parent, {
                    position: busPosition,
                    originalLabel: signalDimension.name,
                    labelType: 'virtual-bus-member',
                    dimensions: [signalDimension.name],
                });
            }
        }
    }
    buildBusEntryDimensionMap(page) {
        const map = new Map();
        for (const netBus of page.NetBus ?? []) {
            const memberDims = (0, OrcadDsnBusUtils_1.getNetBusMemberDimensionsByName)(netBus, (0, OrcadDsnBusUtils_1.inferNetBusName)(netBus));
            const dimByName = new Map(memberDims.map((m) => [m.name, m.index]));
            for (const netScalar of netBus.NetScalar ?? []) {
                if (!netScalar.Name) {
                    continue;
                }
                const dimension = dimByName.get(netScalar.Name);
                if (!dimension) {
                    continue;
                }
                for (const wire of netScalar.WireScalars ?? []) {
                    map.set(`${wire.StartX},${wire.StartY}`, { name: netScalar.Name, index: dimension });
                    map.set(`${wire.EndX},${wire.EndY}`, { name: netScalar.Name, index: dimension });
                }
            }
            for (const wireBus of netBus.WireBus ?? []) {
                map.set(`${wireBus.StartX},${wireBus.StartY}`, wireBus.Name || netBus.Name);
                map.set(`${wireBus.EndX},${wireBus.EndY}`, wireBus.Name || netBus.Name);
            }
        }
        return map;
    }
    *generateFromNetScalar(parent, netScalar) {
        const wires = netScalar.WireScalars ?? [];
        const netScalarId = wires.length >= 2 ? this.netScalarIdentityFor(wires) : undefined;
        for (const wire of wires) {
            yield* this.generateFromWireScalar(parent, wire, netScalarId);
        }
    }
    netScalarIdentityFor(wires) {
        let minDbId;
        for (const wire of wires) {
            if (wire.DbId === undefined) {
                continue;
            }
            if (minDbId === undefined || wire.DbId < minDbId) {
                minDbId = wire.DbId;
            }
        }
        return minDbId === undefined ? undefined : `NS${String(minDbId).padStart(5, '0')}`;
    }
    *generateFromNetBus(parent, netBus) {
        const busName = (0, OrcadDsnBusUtils_1.inferNetBusName)(netBus) ?? netBus.Name;
        const busMembers = (0, OrcadDsnBusUtils_1.getNetBusMemberDimensionsByName)(netBus, busName);
        for (const wire of netBus.WireBus ?? []) {
            const busWidth = Math.max(wire.LineWidth || 0, 1);
            yield new TransformNetWire_1.TransformNetWire(parent, [wire.StartX, wire.StartY], [wire.EndX, wire.EndY], {
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Bus].id],
                drawing: {
                    type: 'segment',
                    start: [wire.StartX, wire.StartY],
                    end: [wire.EndX, wire.EndY],
                    width: busWidth,
                },
            }, 'bus', 'bus', {
                bus: {
                    name: busName,
                    members: busMembers,
                },
            });
            for (const { name, index } of busMembers) {
                const dimensionName = (0, OrcadDsnBusUtils_1.busDimensionName)({
                    index,
                    name,
                });
                const strategyMetadata = {
                    'bus-member': {
                        busName: netBus.Name,
                        memberName: name,
                        memberIndex: index,
                    },
                };
                yield new TransformNetWire_1.TransformNetWire(parent, [wire.StartX, wire.StartY], [wire.EndX, wire.EndY], {
                    layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Bus].id],
                    drawing: {
                        type: 'segment',
                        start: [wire.StartX, wire.StartY],
                        end: [wire.EndX, wire.EndY],
                        width: busWidth,
                    },
                }, dimensionName, dimensionName, strategyMetadata);
                yield new TransformNetLabel_1.TransformNetLabel(parent, {
                    position: [wire.StartX, wire.StartY],
                    originalLabel: name,
                    labelType: 'virtual-bus-member',
                    dimensions: [dimensionName],
                    strategyMetadata: strategyMetadata,
                });
            }
            for (const alias of wire.Alias ?? []) {
                if (!alias.Name) {
                    continue;
                }
                const labelPos = [alias.LocX ?? wire.StartX, alias.LocY ?? wire.StartY];
                yield new TransformNetLabel_1.TransformNetLabel(parent, {
                    position: labelPos,
                    originalLabel: alias.Name,
                    labelType: 'alias',
                    dimensions: ['bus'],
                });
            }
        }
        for (const netScalar of netBus.NetScalar ?? []) {
            yield* this.generateFromNetScalar(parent, netScalar);
        }
    }
    *generateFromWireScalar(parent, wire, netScalarId) {
        const from = [wire.StartX, wire.StartY];
        const to = [wire.EndX, wire.EndY];
        const wireWidth = Math.max((wire.LineWidth || 0) * 0.1, 1);
        const systemId = wire.DbId ? `N${String(wire.DbId).padStart(5, '0')}` : undefined;
        const strategyMetadata = { 'source-wire-db-id': wire.DbId };
        if (systemId !== undefined) {
            strategyMetadata['system-id'] = systemId;
        }
        if (wire.UserProperty?.length) {
            strategyMetadata['net-properties'] = wire.UserProperty.map((property) => ({
                name: property.Name,
                value: property.Value,
            }));
        }
        if (netScalarId !== undefined) {
            strategyMetadata['net-scalar-id'] = netScalarId;
        }
        const wireObj = new TransformNetWire_1.TransformNetWire(parent, from, to, {
            layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Wire].id],
            drawing: {
                type: 'segment',
                start: from,
                end: to,
                width: wireWidth,
            },
        }, undefined, undefined, Object.keys(strategyMetadata).length > 0 ? strategyMetadata : undefined);
        yield wireObj;
        for (const junction of wire.Junction ?? []) {
            yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Junction].id],
                drawing: {
                    type: 'junction',
                    position: [junction.LocX, junction.LocY],
                    radiusOverride: 1.25,
                },
            }, wireObj);
        }
        for (const alias of wire.Alias ?? []) {
            if (!alias.Name) {
                continue;
            }
            const netLabel = new TransformNetLabel_1.TransformNetLabel(parent, {
                position: [wire.StartX, wire.StartY],
                originalLabel: alias.Name,
                labelType: 'alias',
            });
            yield netLabel;
            const labelPos = [alias.LocX ?? wire.StartX, alias.LocY ?? wire.StartY];
            const { angle } = OrcadDsnUtils_1.OrcadDsnUtils.orcadRotationToCadstrom(alias.Rotation ?? 0, 0);
            yield new TransformSchematicDrawing_1.TransformSchematicDrawing(parent, {
                layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Label].id],
                drawing: {
                    type: 'text',
                    ...(0, ModelText_1.encodeStrippedText)({ raw: alias.Name, dialects: [ModelText_1.TextDialect.Orcad] }),
                    pos: labelPos,
                    angle,
                    justify: [-1, 1],
                    size: alias.Font ? (0, OrcadDsnSymbolUtils_1.scaleFont)(alias.Font) : 1,
                    thickness: 1,
                },
            }, netLabel);
        }
    }
}
exports.OrcadDsnTransformNetWireGenerator = OrcadDsnTransformNetWireGenerator;
//# sourceMappingURL=OrcadDsnTransformNetWireGenerator.js.map